import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Underline } from 'components';
import { wrap } from 'utils/wrap';
import { useWindowSize } from 'hooks';
import PrevArrow from 'images/shared/PrevArrow';

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    };
  }
};

const TenQuestions = () => {
  const { windowWidth } = useWindowSize();

  const slides = [
    {
      id: 1,
      title: 'The Key Challenge',
      text: '<strong>If you were explaining this project to a friend, how would you describe it?</strong><br />What&apos;s the heart of what you&apos;re trying to do—and why now?'
    },
    {
      id: 2,
      title: "What's already working?",
      text: '<strong>What feels good at the moment?</strong><br />What&apos;s gaining traction, showing momentum, or worth protecting and building on?'
    },
    {
      id: 3,
      title: "What's not quite there yet?",
      text: "<strong>What's been more frustrating than fun?</strong><br />Which parts of the project need the most attention—and in what order?"
    },
    {
      id: 4,
      title: 'Looking ahead',
      text: '<strong>Fast-forward 12-24 months. What does success look like?</strong><br />What would make you proud when it comes to awareness, engagement, or performance?'
    },
    {
      id: 5,
      title: "Knowing it's working",
      text: "<strong>How will we know we've nailed it?</strong><br />Are we talking reach, engagement, leads, revenue, new clients, referrals, awards—or something else entirely?"
    },
    {
      id: 6,
      title: "Who we're really talking to?",
      text: '<strong>Who are the priority audiences?</strong><br />Tell us about your top two or three customer types—their roles, behaviours, motivations, and preferences. Any existing research is very welcome.'
    },
    {
      id: 7,
      title: 'Budget & timing',
      text: '<strong>Do you have a guide investment in mind—and over what timeframe?</strong><br />Even a rough range helps us explore the right options and focus our thinking from the start.'
    },
    {
      id: 8,
      title: 'Inspiration & references',
      text: "<strong>Have you seen any work similar to what you're imagining that you liked?</strong><br />What stood out to you—visually, emotionally, or strategically?"
    },
    {
      id: 9,
      title: 'Things to watch out for',
      text: "<strong>Any obstacles, sensitivities, or hard no's we should know about?</strong><br />Past experiences, preferences, quirks, or things you'd rather avoid repeating?"
    },
    {
      id: 10,
      title: 'Anything else?',
      text: "<strong>Is there anything we haven't asked that feels important to mention?</strong><br />This is your space for context, gut feelings, or details that don't fit neatly elsewhere."
    }
  ];

  const [[page, direction], setPage] = useState([10, 0]);

  const paginate = (dir) => {
    setPage([page + dir, dir]);
  };

  const selectPage = (selectedPage) => {
    setPage([selectedPage, 1]);
  };

  const index = wrap(0, slides.length, page);


  return (
    <>
      <ModulesTitle aria-label="Considerations">
        <span>
          <span>Things to consider</span>
          <Underline color="var(--ax-gold)" width={2.5} />
        </span>
      </ModulesTitle>
      <NumberContainer
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <NumberBox
            key={i}
            onClick={(e) => {
              e.stopPropagation();
              selectPage(i);
            }}
            aria-label={`Consideration ${i + 1} - Click to see more information`}
            selected={slides[index].id === i + 1}>
            <span>{String(i + 1).padStart(2, '0')}</span>
          </NumberBox>
        ))}
        <Prev
          onClick={() => paginate(-1)}
          disabled={page === 0}
          aria-label="Previous consideration"
        />
        <Next
          onClick={() => paginate(1)}
          disabled={page === 9}
          id="omnichannel-carousel-click"
          aria-label="Next consideration"
        />
      </NumberContainer>
      <Container
        key={page}
        id={slides[index]}
        alt="slide"
        custom={direction}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{
          duration: 0.5
        }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={1}
        onDragEnd={(e, { offset, velocity }) => {
          const swipe = swipePower(offset.x, velocity.x);

          if (swipe < -swipeConfidenceThreshold) {
            paginate(1);
          } else if (swipe > swipeConfidenceThreshold) {
            paginate(-1);
          }
        }}>
        <AnimatePresence initial={false} custom={direction}>
          <Slide>
            <TextContainer>
              {slides.map((slide) => (
                <div
                  key={slide.id}
                  className={`carousel-slide${
                    slides[index].id === slide.id ? ' carousel-slide--active' : ''
                  }`}
                  aria-hidden={slides[index].id !== slide.id}>
                  <SlideTitle>
                    <h4 className="carousel-slide-number">
                      {slide.id === 10 ? 10 : `0${slide.id}`}
                    </h4>
                    <h5
                      aria-label={slide.title}
                      dangerouslySetInnerHTML={{
                        __html:
                          windowWidth < 1200
                            ? slide.title.replace('<br aria-hidden="true" />', ' ')
                            : slide.title
                      }}
                    />
                  </SlideTitle>
                  <p dangerouslySetInnerHTML={{ __html: slide.text }} />
                </div>
              ))}
            </TextContainer>
          </Slide>
        </AnimatePresence>
      </Container>
    </>
  );
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const ModulesTitle = styled.h3`
  justify-self: center;
  color: black;
  font-size: 2rem;
  line-height: 1.25em;
  margin-bottom: 1em;
  letter-spacing: 0px;
  grid-column: 2/3;
  text-align: center;

  span {
    position: relative;
    z-index: 1;

    svg {
      position: absolute;
      bottom: -0.125em;
      left: 0;
      width: 100%;
      z-index: 0;
    }
  }

  @media (min-width: 48em) {
    font-size: 3rem;
  }
`;

const Container = styled(motion.div)`
  align-items: center;
  display: flex;
  grid-column-gap: 1.25rem;
  grid-column: 1/6;
  grid-row-gap: 2.5rem;
  height: auto;
  justify-content: center;
  max-width: 100%;
  overflow-x: hidden !important;
  position: relative;

  @media (min-width: 1150px) {
    grid-column: 2/12;
  }

  .carousel-slide {
    display: none;
  }
  .carousel-slide--active {
    display: block;
  }
`;

const Slide = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 3rem;
  position: relative;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const SlideTitle = styled.div`
  display: flex;
  gap: 0.75rem;
  width: auto;
  margin-bottom: 0.5em;
  h5 {
    color: black;
    font-size: 2.5rem;
    letter-spacing: 0px;
    line-height: 1em;
    margin: 0;

    @media (min-width: 768px) {
      font-size: 3rem;
      text-align: left;
      width: 100%;
    }
  }

  h4 {
    width: auto;
    font-size: 2rem;
    line-height: 1.2em;
    font-weight: bold;
    margin-top: 0;
    margin-bottom: 0;
    color: var(--ax-gold);
    text-transform: uppercase;
    @media (min-width: 768px) {
      font-size: 2rem;
      line-height: 1.9em;
    }
  }
  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const TextContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;

  p {
    color: black;
    font-size: 1.25rem;
    line-height: 1.5em;
    strong {
      font-size: 1.5rem;
    }
  }
`;

const Prev = styled(PrevArrow)`
  display: none;
  color: ${({ disabled }) => (disabled ? 'grey' : '#CE9200')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  @media (min-width: 48rem) {
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: block;
    left: 0;
    position: absolute;
    top: 45%;
    width: 2.563em;
  }

  @media (min-width: 65.625rem) {
    left: 8rem;
  }

  @media screen and (min-width: 71.875rem) {
    left: -4.5rem;
    top: 37%;
  }
`;

const Next = styled(PrevArrow)`
  display: none;
  color: ${({ disabled }) => (disabled ? '#C1BEAC' : 'var(--ax-gold)')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  @media (min-width: 48rem) {
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: block;
    position: absolute;
    right: 0;
    top: 45%;
    transform: rotate(180deg);
    width: 2.563em;
  }

  @media (min-width: 65.625rem) {
    right: 8rem;
  }

  @media screen and (min-width: 71.875rem) {
    top: 37%;
    right: -4.5rem;
  }
`;

const NumberContainer = styled.div`
  display: grid;
  grid-column: 1/6;
  grid-gap: 0.5rem;
  grid-template-columns: repeat(5, minmax(40px, 100px));
  justify-content: center;
  margin-bottom: 2rem;
  margin: 0 auto 2.25em;
  max-width: 940px;
  position: relative;
  transition: 0.75s;
  width: 100%;

  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
  @media (min-width: 1150px) {
    grid-template-columns: repeat(10, minmax(40px, 100px));
    grid-column: 3/11;
  }
`;

const NumberBox = styled.button`
  background: ${({ selected }) => (selected ? 'var(--ax-gold) ' : 'var(--ex-beige)')};
  border: 1px solid var(--ax-gold);
  cursor: pointer;
  font-weight: bold;
  height: 0;
  outline: none;
  padding-top: 100%;
  position: relative;
  transition: 0.75s;
  width: 100%;

  :hover,
  :focus {
    border: 1px solid var(--ax-gold);
    outline: none;
  }
  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.5rem;
    color: ${({ selected }) => (selected ? 'white' : '#C1BEAC')};
    @media (min-width: 768px) {
      font-size: 2rem;
    }
  }
`;

export default TenQuestions;
